import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';

export default function AgentSelector({ selectedAgentId, onSelect, excludeReferrers = true, teamLeaderCode = null }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAgents = async () => {
      try {
        setLoading(true);
        const agentsRef = collection(db, 'plsss_agents');
        
        const agentsData = [];
        
        if (teamLeaderCode) {
          // First, add the team leader themselves
          const teamLeaderQuery = query(
            agentsRef,
            where('agent_id', '==', teamLeaderCode),
            where('status', '==', 'Active')
          );
          const teamLeaderSnapshot = await getDocs(teamLeaderQuery);
          
          teamLeaderSnapshot.forEach((doc) => {
            const data = doc.data();
            const role = data.apps?.agents?.role || data.role || 'user';
            
            // Exclude referrers if specified
            if (excludeReferrers && role === 'referrer') return;
            
            agentsData.push({
              id: doc.id,
              agent_id: data.agent_id,
              name: data.name,
              email: data.email,
              role: role,
              status: data.status || 'Active',
              isTeamLeader: true
            });
          });
          
          // Then, add team members under this team leader
          const teamMembersQuery = query(
            agentsRef,
            where('apps.agents.teamLeader', '==', teamLeaderCode),
            where('status', '==', 'Active')
          );
          const teamMembersSnapshot = await getDocs(teamMembersQuery);
          
          teamMembersSnapshot.forEach((doc) => {
            const data = doc.data();
            const role = data.apps?.agents?.role || data.role || 'user';
            
            // Exclude referrers if specified
            if (excludeReferrers && role === 'referrer') return;
            
            agentsData.push({
              id: doc.id,
              agent_id: data.agent_id,
              name: data.name,
              email: data.email,
              role: role,
              status: data.status || 'Active',
              isTeamLeader: false
            });
          });
        } else {
          // Load all agents
          const snapshot = await getDocs(agentsRef);
          
          snapshot.forEach((doc) => {
            const data = doc.data();
            const role = data.apps?.agents?.role || data.role || 'user';
            const status = data.status || 'Active';
            
            // Only include active agents
            if (status !== 'Active') return;
            
            // Exclude referrers if specified
            if (excludeReferrers && role === 'referrer') return;
            
            agentsData.push({
              id: doc.id,
              agent_id: data.agent_id,
              name: data.name,
              email: data.email,
              role: role,
              status: status
            });
          });
        }
        
        // Sort by name
        agentsData.sort((a, b) => a.name.localeCompare(b.name));
        
        setAgents(agentsData);
      } catch (error) {
        console.error('Error loading agents:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadAgents();
  }, [excludeReferrers, teamLeaderCode]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Select Agent {excludeReferrers && <span className="text-gray-500">(Admin & Users only)</span>}
      </label>
      <select
        value={selectedAgentId || ''}
        onChange={(e) => onSelect(e.target.value)}
        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
      >
        <option value="">-- Select an agent --</option>
        {agents.map((agent) => (
          <option key={agent.id} value={agent.agent_id}>
            {agent.name} ({agent.agent_id}) - {agent.role}
          </option>
        ))}
      </select>
      {agents.length === 0 && (
        <p className="mt-2 text-sm text-gray-500">No agents available</p>
      )}
    </div>
  );
}
